import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, AfterViewChecked } from '@angular/core';
import { FeedCardsService } from './service/feed-cards.service';
import { ActivatedRoute, Scroll, Router } from '@angular/router';
import { Logger } from '@app/core/logging';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, delay, filter, tap, take, skip } from 'rxjs/operators';
import { intervals, inInterval } from './feed-page.utils';
import { Persist } from '@app/analytics/actions/data-layer';
import { Store, select } from '@ngrx/store';
import { IAppState } from '@app/store';
import { FeedCard } from '@vestiaire/api-sdk-angular';
import * as fromFeed from './store/reducers';
import * as feedActions from './store/actions';
import * as localForage from 'localforage';

import { cardsArray } from './store/schema/feed.schema';
import { normalize, denormalize } from 'normalizr';
import { ViewportScroller } from '@angular/common';

@Component({
  selector: 'vc-feed-page',
  templateUrl: './feed-page.component.html',
  styleUrls: ['./feed-page.component.scss']
})
export class FeedPageComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  public urlApp = null;

  cards: FeedCard[];
  labelMap = {};
  observer: IntersectionObserver;
  offsetInterval = 0;
  numberUpdates: number;
  hasNewerCards$: Observable<number>;
  next: string;
  private ngUnsubscribe = new Subject<void>();
  lastSeenCardId: string;
  feedType: string;
  likedItems: string[] = [];
  @ViewChild('scroller', { static: false }) scroller: ElementRef<HTMLElement>;

  scrollPosition: [number, number] = [0, 0];
  scrollPositionSubscription: Subscription = new Subscription();
  returnInSession = false;

  constructor(
    private route: ActivatedRoute,
    private logger: Logger,
    private feedService: FeedCardsService,
    protected readonly platformService: PlatformService,
    private store: Store<IAppState>,
    private router: Router,
    private viewportScroller: ViewportScroller
  ) {
    this.setAppUrlByOS();
    // If There is a Scroll event submit by the router
    // with an y position > 0, that means we are back from a page visited (back button/back gesture)
    // so we restore the state and scroll back to the position the user were previously
    // Angular do it natively for sync datas, since newsfeed is async, we need this trick
    this.scrollPositionSubscription = this.router.events
      .pipe(
        filter((event: Scroll) => event instanceof Scroll),
        tap((event: Scroll) => {
          this.scrollPosition = event.position ? event.position : [0, 0];
          this.returnInSession = event.position ? true : false;
        })
      )
      .subscribe(
        () => {},
        error => console.error
      );
  }

  ngOnInit() {
    if (this.platformService.isBrowser) {
      // We want to restore the feed state, if an user goes from the feed to another page and comeback
      if (this.returnInSession) {
        this.store.pipe(select(fromFeed.getState), take(1)).subscribe(
          state => {
            if (state.cards) {
              this.cards = denormalize(state.map, cardsArray, {
                cards: state.cards,
                actor: state.users,
                orderedProducts: state.products
              });
            }
          },
          error => {
            this.logger.error('No state for Feed Page', error);
          }
        );

        localForage.getItem('likedItem').then((res: any) => {
          if (res.products) {
            this.likedItems = res.products;
          }
        });
      }
      if (!this.returnInSession) {
        this.route.data.subscribe({
          next: data => {
            this.feedService.init(data.feedType);
            this.feedType = this.feedService.feedType;
          },
          error: console.error
        });
      } else {
        this.feedService.initCheckFeedStatus();
      }
    }
  }

  ngAfterViewInit() {
    if (this.platformService.isBrowser) {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          this.feedService.fetchOlderCards(this.cards);
        }
      }, {});
      if (this.scroller && this.observer) {
        this.observer.observe(this.scroller.nativeElement);
      }
      this.route.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe(data => {
        if (data.pageAnalytics) {
          this.store.dispatch(new Persist(data.pageAnalytics));
        }
      }, this.logger.error);
      this.viewportScroller.scrollToPosition(this.scrollPosition);
      this.lastSeenCardId = localStorage.getItem('lastCardSeen');
    }
    this.hasNewerCards$ = this.feedService.hasNewerCards$.pipe(delay(0));
    this.feedService.cards$.pipe(takeUntil(this.ngUnsubscribe), delay(0)).subscribe({
      next: cards => {
        if (cards.length > 0 && !this.returnInSession) {
          this.labelMap = this.createCardLabelMap(cards);
          this.cards = cards;
          const normalized = normalize(this.cards, cardsArray);
          this.store.dispatch(new feedActions.SetFeedState(normalized));
        }
        this.returnInSession = false;
      },
      error: this.logger.error
    });
  }

  ngAfterViewChecked() {
    if (this.scroller && this.observer) {
      this.observer.observe(this.scroller.nativeElement);
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.feedService.killInterval();
    if (this.platformService.isBrowser) {
      this.saveLastCardSeenId();
      this.observer.disconnect();
    }
    this.scrollPositionSubscription.unsubscribe();
  }

  createCardLabelMap(cards: FeedCard[]): Object {
    const labelMap = {};
    const usedIntervals = new Set();
    for (const card of cards) {
      if (card.cardType === 'recommended-profiles') {
        continue;
      }
      for (const interval of intervals) {
        if (!usedIntervals.has(interval) && inInterval(interval, new Date(card.updatedAt))) {
          labelMap[card.id] = interval.label;
          usedIntervals.add(interval);
          break;
        }
      }
    }
    return labelMap;
  }

  setAppUrlByOS() {
    this.store.select('device').subscribe(
      res => {
        this.urlApp = res.isAndroid ? 'https://app.adjust.com/2ep0vf' : 'https://app.adjust.com/xa7pe2';
      },
      error => {
        console.log(error);
      }
    );
  }

  loadNewer() {
    const currentCards = this.cards;
    // Skip the first one (current state), take the next one (new state)
    this.feedService.cards$.pipe(skip(1), take(1)).subscribe({
      next: newCards => {
        // We ignore recommanded profiles because it does not looks like a card for the user
        const l = newCards.filter(card => card.cardType !== 'recommended-profiles').length;
        const l2 = currentCards.filter(card => card.cardType !== 'recommended-profiles').length;
        this.numberUpdates = l - l2;
      },
      error: console.error
    });

    this.saveLastCardSeenId();
    this.lastSeenCardId = localStorage.getItem('lastCardSeen');

    if (window) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    this.feedService.fetchNewerCards();
  }

  saveLastCardSeenId() {
    if (this.cards.length && this.feedType === 'default') {
      const lastSeenCard = this.cards.find(card => card.actor.isFollowed === true);
      if (lastSeenCard) {
        localStorage.setItem('lastCardSeen', lastSeenCard.id);
      }
    }
  }
}
